import { z } from 'zod';
// Money carries its currency (the money concept — ems-domain DOMAIN-RULES; the
// books' money line — ADR-063): an amount is stored and shown with the ISO 4217
// code it was entered in, the platform never converts, a total is per currency.
// The CATALOGUE is the runtime's — `Intl.supportedValuesOf('currency')`, named
// by `Intl.DisplayNames` in the viewer's language — as the time zones are
// (ADR-030): no table, nothing to load, the same list on both sides of the wire.
// A code is validated by MEMBERSHIP in it (a formatter accepts any three letters,
// so, unlike a zone, constructing one tests nothing). ONE project currency
// (`projects.default_currency`) is the DEFAULT of every money field, never a
// rule — an amount keeps the currency it was entered in.
/** The codes the runtime knows, as ISO 4217 spells them (upper-case). */
export const CURRENCY_CODES = new Set(Intl.supportedValuesOf('currency'));
export const isCurrencyCode = (code) => CURRENCY_CODES.has(code);
/** A currency on the wire: three letters, upper-cased, one the runtime knows. */
export const CurrencyCodeSchema = z.string().trim().toUpperCase().length(3).refine(isCurrencyCode, { message: 'UNKNOWN_CURRENCY' });
const MINOR_UNITS = new Map();
/** The currency's minor units (SAR 2 · LBP 0 · BHD 3) — how many decimals an amount in it may count. */
export function minorUnitsOf(code) {
    let n = MINOR_UNITS.get(code);
    if (n === undefined) {
        n = new Intl.NumberFormat('en', { style: 'currency', currency: code }).resolvedOptions().maximumFractionDigits ?? 2;
        MINOR_UNITS.set(code, n);
    }
    return n;
}
/** The decimals an amount carries — read on the shortest form that still identifies it (15 significant digits; the
 *  books column holds 14), so the float's binary noise never counts (1234567.89 counts two, 0.1 + 0.2 one) and an
 *  exponent form is read right (1e-7 counts seven). */
export function decimalsOf(amount) {
    if (!Number.isFinite(amount))
        return 0;
    const [mantissa, exponent] = amount.toPrecision(15).split('e');
    const frac = (mantissa.split('.')[1] ?? '').replace(/0+$/, '');
    return Math.max(0, frac.length - Number(exponent ?? 0));
}
/** An amount fits its currency when it counts no more minor units than the currency has (SAR 12.345 does not; BHD 12.345 does). */
export function fitsMinorUnits(amount, code) {
    return decimalsOf(amount) <= minorUnitsOf(code);
}
/** The refinement every money pair shares — a cost beside its currency counts the currency's minor units at most;
 *  one of the two absent, nothing is judged here: a PATCH may carry one half against a stored other half, so the
 *  write judges the merged pair — and refuses a cost that would stand without a currency (`COST_CURRENCY_REQUIRED`). */
export function refineMoney(amount, code, ctx, path) {
    if (amount == null || !code || !isCurrencyCode(code))
        return;
    if (!fitsMinorUnits(amount, code))
        ctx.addIssue({ code: 'custom', path: [path], message: 'COST_DECIMALS' });
}
